import React, { useState, useEffect, useCallback } from 'react';
import './WebSocketStatus.css';
import websocketService from '../../services/websocketService';
import { isAuthenticated } from '../../services/auth';

/**
 * WebSocketStatus - Displays real-time WebSocket connection status
 * Shows: Connection state, active subscriptions count, last update time
 *
 * Visual states:
 * - Connected (green): WebSocket is connected and receiving data
 * - Connecting (yellow): Initial connection attempt
 * - Reconnecting (yellow): Attempting to reconnect after disconnect
 * - Disconnected (red): WebSocket is disconnected
 */
const WebSocketStatus = () => {
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [activeStreams, setActiveStreams] = useState(0);

  // Expected streams we track
  const expectedStreams = ['positions', 'pnl', 'option-chain', 'indices', 'derived-data'];

  // Handle connection status changes from websocketService
  const handleStatusChange = useCallback((status) => {
    setConnectionStatus(status);

    // Update active streams count when connected
    if (status === 'Connected') {
      const activeCount = Array.from(websocketService.subscriptions.keys()).filter(
        stream => expectedStreams.includes(stream)
      ).length;
      setActiveStreams(activeCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // expectedStreams is a constant array

  // Handle data received from any stream
  const handleWebSocketData = useCallback(() => {
    setLastUpdateTime(new Date());
    // Ensure status is connected when we receive data
    if (connectionStatus !== 'Connected') {
      setConnectionStatus('Connected');
    }
  }, [connectionStatus]);

  useEffect(() => {
    // Don't connect if not authenticated
    if (!isAuthenticated()) {
      return;
    }

    // Register status change listener
    websocketService.addStatusChangeListener(handleStatusChange);

    // Register handlers for all streams to detect activity
    expectedStreams.forEach(stream => {
      websocketService.addMessageHandler(stream, handleWebSocketData);
    });

    // Get initial status
    const initialStatus = websocketService.getConnectionStatus();
    setConnectionStatus(initialStatus);

    // Cleanup on unmount
    return () => {
      websocketService.removeStatusChangeListener(handleStatusChange);
      expectedStreams.forEach(stream => {
        websocketService.removeMessageHandler(stream, handleWebSocketData);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleStatusChange, handleWebSocketData]); // expectedStreams is stable

  // Don't render if not authenticated
  if (!isAuthenticated()) {
    return null;
  }

  // Format time in IST
  const formatTime = (date) => {
    if (!date) return '';
    return date.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Determine UI state based on connection status
  const isConnected = connectionStatus === 'Connected';
  const isConnecting = connectionStatus === 'Connecting' || connectionStatus === 'Reconnecting';

  // CSS class for styling
  const statusClass = isConnected ? 'connected' : isConnecting ? 'connecting' : 'disconnected';

  // Status label text
  const getStatusLabel = () => {
    if (isConnected) return 'WS';
    if (connectionStatus === 'Reconnecting') return 'RECONNECTING';
    if (connectionStatus === 'Connecting') return 'CONNECTING';
    return 'WS OFF';
  };

  return (
    <div className={`websocket-status ${statusClass}`}>
      <div className="ws-indicator">
        <span className={`status-dot ${statusClass}`}></span>
        <span className="status-label">
          {getStatusLabel()}
        </span>
        {isConnected && activeStreams > 0 && (
          <span className="connection-count">({activeStreams}/{expectedStreams.length})</span>
        )}
      </div>
      {lastUpdateTime && (
        <span className="last-update">{formatTime(lastUpdateTime)}</span>
      )}
    </div>
  );
};

export default React.memo(WebSocketStatus);
